import React, { useEffect, useMemo, useState } from 'react';
import CascaDeModal from './shared/CascaDeModal';
import AvisoDeCotasDivergentes from './AvisoDeCotasDivergentes';
import { rateioService } from '../services/api';
import { esquecerRateio, useRateio } from '../hooks/useRateio';
import { fmtCompetencia, fmtDateTime } from '../utils/formatters';
import { ComRateio, TEXTO_DO_CRITERIO, fmtPercentual } from '../utils/rateioDoGrupo';
import { CotasDivergentes, lerCotasDivergentes } from '../utils/cotasDivergentes';
import { MotorLigado } from '../utils/motorDoRateio';
import { SimulacaoDaDivisao, valorNoItem } from '../utils/redividir';
import {
  CandidatoDoGrupo,
  ListaDoRateio,
  Selecao,
  candidatosDoGrupo,
  corpoDasListas,
  listaDoProduto,
  moverProduto,
  problemasDaSelecao,
} from '../utils/listasDoRateio';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import AplicarAsCotas from './AplicarAsCotas';
import { precisaAplicar } from '../utils/aplicarCotas';

/**
 * "O QUE O GRUPO COMPARTILHA": as duas listas do rateio explicadas pela FUNÇÃO, o histórico de cada
 * versão e a edição com simular → conferir → gravar. Prévia aprovada em 06/10/2026:
 * https://claude.ai/artifact/CkY7t7PcWW9VTnVUTK4r8x. Regras e contrato em utils/listasDoRateio.ts.
 *
 * ⛔ Os textos do backend vão como vêm: `listasNaoRegistradasMotivo`, `totalMedidoRessalva` e a
 * mensagem do 409. ⛔ Nenhum número é somado aqui: todo valor vem da simulação com nome.
 */

const brl = (v: number) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const comSinal = (d: number) => `${d > 0 ? '+' : d < 0 ? '−' : ''}${brl(Math.abs(d))}`;
const competenciaCorrente = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const th = 'text-[10px] uppercase tracking-wider text-slate-500 font-medium py-1';

interface AssinaturaDoGrupo {
  clientId: number;
  status?: string | null;
  items?: Array<{ productId?: number | null; productCode?: string | null; name?: string | null; type?: string | null }> | null;
}

/** O que a tela diz quando a lista do que se divide não se edita, conforme o modelo DECLARADO. */
function porQueNaoSeEdita(modelo: ComRateio['modeloDoPool']): string | null {
  if (modelo === 'composto') return null;
  if (modelo === 'afirmado') {
    return 'O valor a dividir é um ajuste afirmado, não a soma de serviços. Por isso esta lista não se edita aqui: acrescentar um serviço cobraria esse serviço duas vezes, uma no item e outra dentro do ajuste.';
  }
  return 'Este grupo ainda não tem o modelo do que se divide declarado. Até alguém declarar, esta lista não se edita aqui.';
}

function useNomeDoProduto(rr: ComRateio, candidatos: CandidatoDoGrupo[]) {
  return useMemo(() => {
    const nomes = new Map<number, string>();
    for (const c of candidatos) nomes.set(c.productId, c.nome);
    for (const i of rr.itens?.vigente || []) if (i.name || i.productCode) nomes.set(i.productId, String(i.name || i.productCode));
    return (id: number) => nomes.get(id) || `produto #${id}`;
  }, [rr, candidatos]);
}

/** Partes 1 e 2 da prévia: as duas listas pela função e o histórico das versões. */
/** O histórico das versões: o que cada uma dividia e o que decidia. Vive no painel das listas e, na gaveta do rateio
 *  (10/10/2026), na aba Histórico. */
function TabelaDoHistorico({ rateio: rr, nomeDoProduto }: { rateio: ComRateio; nomeDoProduto: (id: number) => string }) {
  if ((rr.versoes || []).length === 0) return null;
  return (
    <div data-testid='historico-das-listas'>
      <p className='text-xs font-medium text-slate-400 mb-1'>Histórico</p>
      <div className='overflow-x-auto'>
        <table className='w-full text-xs'>
          <thead><tr><th className={`${th} text-left`}>Versão</th><th className={`${th} text-left`}>Vale a partir de</th><th className={`${th} text-left`}>Critério</th><th className={`${th} text-right`}>Valor a dividir</th><th className={`${th} text-left`}>O que dividia / o que decidia</th></tr></thead>
          <tbody>
            {rr.versoes.map((v) => (
              <tr key={v.id} className='border-t border-slate-800 align-top'>
                <td className='py-1.5 text-slate-300'>{v.id}</td>
                <td className='py-1.5 text-slate-300'>{v.competenciaInicio ? fmtCompetencia(v.competenciaInicio) : 'sem data (anterior)'}</td>
                <td className='py-1.5 text-slate-300'>{TEXTO_DO_CRITERIO[v.criterio]}</td>
                <td className='py-1.5 text-right tabular-nums text-slate-300'>{v.poolSnapshot != null ? brl(v.poolSnapshot) : '—'}</td>
                <td className='py-1.5'>
                  {v.listasRegistradas === false ? (
                    // ⛔ Como vem do backend: a tela não redige o seu nem preenche com as listas de hoje.
                    <span className={TONE_TEXT.warning} data-testid={`motivo-versao-${v.id}`}>{v.listasNaoRegistradasMotivo || ''}</span>
                  ) : (
                    <span className='text-slate-300'>
                      {(v.produtoIds || []).map(nomeDoProduto).join(', ')} / {(v.produtoIdsBase || []).map(nomeDoProduto).join(', ')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** O histórico sozinho (aba Histórico da gaveta do rateio). */
export function HistoricoDasListas({ rateio: rr, contracts }: { rateio: ComRateio; contracts: AssinaturaDoGrupo[] }) {
  const empresas = rr.linhas.map((l) => l.clientId);
  const candidatos = useMemo(() => candidatosDoGrupo(contracts, empresas), [contracts, empresas.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const nomeDoProduto = useNomeDoProduto(rr, candidatos);
  return <TabelaDoHistorico rateio={rr} nomeDoProduto={nomeDoProduto} />;
}

export function PainelDasListas({ rateio: rr, contracts, nome, onEditar, semHistorico }: {
  rateio: ComRateio;
  contracts: AssinaturaDoGrupo[];
  nome: (clientId: number) => string;
  /** Só admin: abre a edição. */
  onEditar?: () => void;
  /** Na gaveta do rateio o histórico tem aba própria. */
  semHistorico?: boolean;
}) {
  const empresas = rr.linhas.map((l) => l.clientId);
  const candidatos = useMemo(() => candidatosDoGrupo(contracts, empresas), [contracts, empresas.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const nomeDoProduto = useNomeDoProduto(rr, candidatos);
  const naoSeEdita = porQueNaoSeEdita(rr.modeloDoPool ?? null);
  const semListas = !rr.produtoIds || !rr.produtoIdsBase;

  return (
    <div className='space-y-3' data-testid='painel-das-listas'>
      <div className='flex items-center gap-2'>
        <span className='font-medium text-white text-sm'>O que o grupo compartilha</span>
        {onEditar && !semListas && (
          <button type='button' onClick={onEditar} className='ml-auto text-xs px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200'>
            Editar as listas
          </button>
        )}
      </div>
      {semListas ? (
        <p className={`text-xs rounded-lg border px-3 py-2 ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`} data-testid='listas-sem-registro'>
          {rr.listasNaoRegistradasMotivo || ''}
        </p>
      ) : (
        <div className='grid gap-3 md:grid-cols-2'>
          <div className={`rounded-lg border p-3 space-y-2 ${TONE_BORDER.group} ${TONE_SURFACE.group}`} data-testid='lista-divide'>
            <p className={`text-sm font-medium ${TONE_TEXT.group}`}>O que se divide</p>
            <p className='text-xs text-slate-400'>Itens que o grupo usa junto. Somados, formam o valor que é repartido entre as empresas.</p>
            {(rr.itens?.vigente || []).map((i) => (
              <div key={i.productId} className='flex justify-between gap-2 text-sm tabular-nums'>
                <span className='text-slate-200 min-w-0 truncate'>{nomeDoProduto(i.productId)} <span className='text-[11px] text-slate-500 font-mono'>{i.productCode}</span></span>
                <span className={i.valorCheio < 0 ? TONE_TEXT.danger : 'text-slate-200'}>{brl(i.valorCheio)}</span>
              </div>
            ))}
            <div className='flex justify-between text-sm font-medium border-t border-slate-700 pt-1.5'>
              <span className='text-slate-200'>Valor a dividir</span><span className='tabular-nums text-slate-100'>{brl(rr.pool)}</span>
            </div>
            {naoSeEdita && <p className={`text-xs rounded-md border px-2 py-1.5 ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`} data-testid='nao-se-edita'>{naoSeEdita}</p>}
          </div>
          <div className={`rounded-lg border p-3 space-y-2 ${TONE_BORDER.regua} ${TONE_SURFACE.regua}`} data-testid='lista-decide'>
            <p className={`text-sm font-medium ${TONE_TEXT.regua}`}>O que decide quanto cada um paga</p>
            <p className='text-xs text-slate-400'>Quantidade de cada empresa nestes itens. A parte de cada uma é a fatia dela nessa contagem.</p>
            {(rr.produtoIdsBase || []).map((id) => (
              <div key={id} className='text-sm text-slate-200'>{nomeDoProduto(id)}</div>
            ))}
            {rr.linhas.map((l) => (
              <div key={l.clientId} className='flex justify-between gap-2 text-sm tabular-nums'>
                <span className='text-slate-300 min-w-0 truncate'>{nome(l.clientId)}</span>
                <span className='text-slate-200'>{rr.base.find((b) => b.clientId === l.clientId)?.ramais ?? '—'} → {fmtPercentual(l.percentualVigente)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!semHistorico && <TabelaDoHistorico rateio={rr} nomeDoProduto={nomeDoProduto} />}
    </div>
  );
}

/**
 * O SELETOR DAS DUAS LISTAS: cada produto vai para "divide", "decide" ou nenhuma, nunca para as duas. Extraído do
 * EditarListasDoRateio (09/10/2026), sem mudar nada, para o "Começar a dividir" usar o MESMO seletor.
 */
export function SeletorDasListas({ linhas, selecao, original, podeEditarOQueSeDivide, naoSeEdita, problemas, nome, onEscolher }: {
  linhas: CandidatoDoGrupo[];
  selecao: Selecao;
  original: Selecao;
  podeEditarOQueSeDivide: boolean;
  naoSeEdita: string | null;
  problemas: string[];
  nome: (clientId: number) => string;
  onEscolher: (productId: number, destino: ListaDoRateio | null) => void;
}) {
  const escolher = onEscolher;
  return (
      <div>
        <p className='text-xs font-medium text-slate-400 mb-1'>Produtos das assinaturas ativas do grupo</p>
        {naoSeEdita && <p className={`mb-2 text-xs rounded-md border px-2 py-1.5 ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`}>{naoSeEdita}</p>}
        <div className='divide-y divide-slate-800 rounded-lg border border-slate-700'>
          {linhas.map((c) => {
            const lista = listaDoProduto(selecao, c.productId);
            const travadoNoDivide = !podeEditarOQueSeDivide && original.divide.includes(c.productId);
            const opcao = (destino: ListaDoRateio | null, rotulo: string) => {
              const ativo = lista === destino;
              const desabilitado = (!podeEditarOQueSeDivide && destino === 'divide' && !original.divide.includes(c.productId)) || travadoNoDivide;
              return (
                <button
                  type='button'
                  disabled={desabilitado}
                  aria-pressed={ativo}
                  onClick={() => escolher(c.productId, destino)}
                  className={`px-2 py-0.5 rounded-full text-xs border disabled:opacity-40 ${ativo ? (destino === 'divide' ? `${TONE_BADGE.group} ${TONE_BORDER.group}` : destino === 'decide' ? `${TONE_BADGE.regua} ${TONE_BORDER.regua}` : 'border-slate-500 text-slate-200') : 'border-slate-700 text-slate-400'}`}
                >
                  {rotulo}
                </button>
              );
            };
            return (
              <div key={c.productId} className='flex items-center gap-2 flex-wrap px-3 py-2 text-sm' data-testid={`produto-${c.productId}`}>
                <span className='text-slate-200 min-w-0 flex-1 truncate'>
                  {c.nome}{c.productCode ? <span className='ml-1 text-[11px] text-slate-500 font-mono'>{c.productCode}</span> : null}
                  <span className='block text-[11px] text-slate-500'>{c.empresas.length ? c.empresas.map(nome).join(', ') : 'em nenhuma assinatura ativa hoje'}</span>
                </span>
                <span className='flex gap-1' role='group' aria-label={`Lista de ${c.nome}`}>
                  {opcao('divide', 'divide')}
                  {opcao('decide', 'decide')}
                  {opcao(null, 'nenhuma')}
                </span>
              </div>
            );
          })}
        </div>
        {problemas.length > 0 && (
          <ul className={`mt-2 text-xs ${TONE_TEXT.warning}`} data-testid='problemas-da-selecao'>
            {problemas.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}
      </div>
  );
}

/**
 * O EFEITO DA SIMULAÇÃO, como medição: a tabela por empresa, a tabela por item, a ressalva do backend e o aviso de
 * cotas divergentes. Extraído do EditarListasDoRateio (09/10/2026), sem mudar nada, para o "Começar a dividir".
 */
export function EfeitoDaSimulacao({ sim, nome, nomeDoProduto, divergencia, recalcular, onRecalcular }: {
  sim: SimulacaoDaDivisao;
  nome: (clientId: number) => string;
  nomeDoProduto: (productId: number) => string;
  divergencia: CotasDivergentes | null;
  recalcular: boolean;
  onRecalcular: (v: boolean) => void;
}) {
  const setRecalcular = onRecalcular;
  const naoMedido = <span className='text-slate-500'>não medido</span>;
  return (
      <div className='space-y-3' data-testid='efeito-da-simulacao'>
        {!sim.somaFecha && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>A divisão calculada não fecha 100%. {sim.motivoDoCalculo}</div>}
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead><tr>
              <th className={`${th} text-left`}>Empresa</th>
              <th className={`${th} text-right`}>Cota nova</th>
              <th className={`${th} text-right`}>Resto da assinatura (tudo fora do que se divide)</th>
              <th className={`${th} text-right`}>Total da assinatura (medido)</th>
              <th className={`${th} text-right`}>Diferença por mês</th>
            </tr></thead>
            <tbody>
              {sim.linhas.map((l) => (
                <tr key={l.clientId} className='border-t border-slate-800' data-testid={`efeito-${l.clientId}`}>
                  <td className='py-1.5 text-slate-200'>{l.nome || nome(l.clientId)}</td>
                  <td className='py-1.5 text-right tabular-nums text-slate-200'>{brl(l.depois)}</td>
                  <td className='py-1.5 text-right tabular-nums text-slate-300'>{l.ficaNaAssinatura == null ? naoMedido : brl(l.ficaNaAssinatura)}</td>
                  <td className='py-1.5 text-right tabular-nums text-slate-200'>{l.totalMedido == null ? naoMedido : brl(l.totalMedido)}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${l.delta < 0 ? TONE_TEXT.danger : l.delta > 0 ? TONE_TEXT.open : 'text-slate-400'}`}>{comSinal(l.delta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sim.itens?.length > 0 && (
          <div className='overflow-x-auto'>
            <table className='w-full text-xs'>
              <thead><tr><th className={`${th} text-left`}>O que se divide (candidato)</th><th className={`${th} text-right`}>Valor cheio</th>{sim.linhas.map((l) => <th key={l.clientId} className={`${th} text-right`}>{l.nome || nome(l.clientId)}</th>)}</tr></thead>
              <tbody>
                {sim.itens.map((i) => (
                  <tr key={i.productId} className='border-t border-slate-800'>
                    <td className='py-1 text-slate-300'>{nomeDoProduto(i.productId)}</td>
                    <td className='py-1 text-right tabular-nums text-slate-300'>{brl(i.valorCheio)}</td>
                    {sim.linhas.map((l) => <td key={l.clientId} className='py-1 text-right tabular-nums text-slate-200'>{brl(valorNoItem(i, l.clientId))}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* ⛔ Texto pronto do backend, como vem; o motorAtivo só decide o destaque. */}
        {sim.totalMedidoRessalva && (
          <p
            className={`rounded-lg border px-3 py-2 text-xs ${sim.motorAtivo ? 'border-slate-700 text-slate-300' : `${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`}`}
            data-testid='ressalva-do-total'
          >
            {sim.totalMedidoRessalva}
          </p>
        )}
        {divergencia && <AvisoDeCotasDivergentes divergencia={divergencia} recalcular={recalcular} onRecalcular={setRecalcular} />}
      </div>
  );
}

/** Partes 3 a 5 da prévia: escolher, simular, conferir o efeito e gravar. */
export function EditarListasDoRateio({ rateio: rr, contracts, nome, onClose, onGravado }: {
  rateio: ComRateio;
  contracts: AssinaturaDoGrupo[];
  nome: (clientId: number) => string;
  onClose: () => void;
  onGravado: () => void;
}) {
  const original: Selecao = useMemo(() => ({ divide: [...(rr.produtoIds || [])], decide: [...(rr.produtoIdsBase || [])] }), [rr]);
  const [selecao, setSelecao] = useState<Selecao>(original);
  const [sim, setSim] = useState<SimulacaoDaDivisao | null>(null);
  const [simulada, setSimulada] = useState<Selecao | null>(null);
  const [erroDaSimulacao, setErroDaSimulacao] = useState<string | null>(null);
  const [competencia, setCompetencia] = useState(competenciaCorrente());
  const [observacao, setObservacao] = useState('');
  const [divergencia, setDivergencia] = useState<CotasDivergentes | null>(null);
  const [recalcular, setRecalcular] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /**
   * ⛔ `null` aqui é DESCONHECIDO, não "desligado". Os dois parágrafos do rodapé que falam do motor
   * não renderizam enquanto for null, de propósito: eles afirmam que algo é seguro, e os dois erros
   * possíveis custam coisas muito diferentes. Aparecer cedo dizendo "o motor está desligado" é um
   * erro que o operador não tem como detectar e sobre o qual ele AGE; não aparecer por meio segundo
   * se corrige sozinho e ninguém age em cima. "Carregando..." seria pior que a ausência, porque dá
   * presença visual a uma afirmação que ainda não existe. 403 de não-admin cai no mesmo lugar.
   * `motorAtivo` NÃO serve aqui: ele só existe no resultado da simulação, e estes textos são lidos
   * ANTES de simular.
   */
  const [motor, setMotor] = useState<MotorLigado | null>(null);
  useEffect(() => {
    let vivo = true;
    rateioService
      .estadoDoMotor(rr.matrizId)
      .then((e) => { if (vivo) setMotor(e.motor); })
      .catch(() => { /* desconhecido continua desconhecido: os parágrafos somem */ });
    return () => { vivo = false; };
  }, [rr.matrizId]);

  const empresas = rr.linhas.map((l) => l.clientId);
  const candidatos = useMemo(() => candidatosDoGrupo(contracts, empresas), [contracts, empresas.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const nomeDoProduto = useNomeDoProduto(rr, candidatos);
  const podeEditarOQueSeDivide = rr.modeloDoPool === 'composto';
  const naoSeEdita = porQueNaoSeEdita(rr.modeloDoPool ?? null);
  const problemas = problemasDaSelecao(selecao, rr.criterio, podeEditarOQueSeDivide, original);

  // Produtos que já estão numa lista e não aparecem nas assinaturas de hoje também ficam visíveis.
  const linhas: CandidatoDoGrupo[] = useMemo(() => {
    const ids = new Set(candidatos.map((c) => c.productId));
    const extras = [...original.divide, ...original.decide]
      .filter((id) => !ids.has(id))
      .map((id) => ({ productId: id, productCode: null, nome: nomeDoProduto(id), empresas: [] as number[] }));
    return [...candidatos, ...extras];
  }, [candidatos, original, nomeDoProduto]);

  // Mudou a seleção: a simulação na tela deixa de valer. Zera, sem comparar listas.
  const escolher = (productId: number, destino: ListaDoRateio | null) => {
    setSelecao((s) => moverProduto(s, productId, destino));
    setSim(null);
    setSimulada(null);
    setDivergencia(null);
    setRecalcular(false);
  };

  const simular = async () => {
    if (problemas.length) return;
    setErroDaSimulacao(null);
    setOcupado(true);
    try {
      const r = await rateioService.simularDivisao(rr.matrizId, {
        criterio: rr.criterio,
        ...corpoDasListas(selecao),
        descontosSeguemRateio: rr.descontosSeguemRateio,
      });
      setSim(r);
      setSimulada(selecao);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Não foi possível simular.';
      setErroDaSimulacao(Array.isArray(msg) ? msg.join(' ') : String(msg));
    } finally {
      setOcupado(false);
    }
  };

  const gravar = async () => {
    if (!sim || !simulada) return;
    setOcupado(true);
    setErro(null);
    try {
      await rateioService.salvar(
        rr.matrizId,
        {
          competenciaInicio: competencia,
          poolParaConfirmar: sim.poolParaConfirmar,
          ...(sim.impressaoDaSimulacao ? { impressaoParaConfirmar: sim.impressaoDaSimulacao } : {}),
          criterio: rr.criterio,
          // As MESMAS listas que foram simuladas, as duas explícitas.
          ...corpoDasListas(simulada),
          descontosSeguemRateio: rr.descontosSeguemRateio,
          observacao: observacao.trim(),
        },
        { recalcularCotas: !!divergencia && recalcular },
      );
      esquecerRateio();
      onGravado();
    } catch (e: any) {
      const d = lerCotasDivergentes(e);
      if (d) { setDivergencia(d); setRecalcular(false); }
      else {
        const msg = e?.response?.data?.message || e?.response?.data?.mensagem || e?.message || 'Erro ao gravar';
        setErro(Array.isArray(msg) ? msg.join(' ') : String(msg));
        if (/base mudou desde a simula|conjunto que esta sendo gravado nao e o que foi simulado/i.test(String(msg))) { setSim(null); setSimulada(null); }
      }
    } finally {
      setOcupado(false);
    }
  };

  const bloqueado = ocupado || !sim || !sim.somaFecha || !competencia || !observacao.trim() || (!!divergencia && !recalcular);
  return (
    <CascaDeModal
      largura='lg'
      titulo='O que o grupo compartilha'
      subtitulo={`matriz #${rr.matrizId} · ${TEXTO_DO_CRITERIO[rr.criterio]}`}
      onClose={onClose}
      corpoClassName='p-5 space-y-4'
      rodape={
        <>
          <span className='text-xs text-slate-500'>Grava uma versão nova do rateio.</span>
          <div className='ml-auto flex gap-2'>
            <button type='button' onClick={onClose} className='px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white'>Cancelar</button>
            {!sim ? (
              <button type='button' onClick={simular} disabled={ocupado || problemas.length > 0} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50'>
                {ocupado ? 'Simulando…' : 'Simular a nova divisão'}
              </button>
            ) : (
              <button type='button' onClick={gravar} disabled={bloqueado} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50'>
                {ocupado ? 'Gravando…' : 'Gravar nova divisão'}
              </button>
            )}
          </div>
        </>
      }
    >
      {/* 3. Escolher: cada produto vai para uma lista, ou nenhuma; nunca para as duas. */}
      <SeletorDasListas linhas={linhas} selecao={selecao} original={original} podeEditarOQueSeDivide={podeEditarOQueSeDivide} naoSeEdita={naoSeEdita} problemas={problemas} nome={nome} onEscolher={escolher} />

      {erroDaSimulacao && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>{erroDaSimulacao}</div>}
      {erro && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>{erro}</div>}

      {/* 4. O efeito, como MEDIÇÃO: todo número é campo da simulação, com nome. */}
      {sim && <EfeitoDaSimulacao sim={sim} nome={nome} nomeDoProduto={nomeDoProduto} divergencia={divergencia} recalcular={recalcular} onRecalcular={setRecalcular} />}

      {/* 5. A competência, a observação e o que acontece com as faturas. */}
      <div className='grid grid-cols-3 gap-3'>
        <div>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='ld-comp'>Vale a partir de *</label>
          <input id='ld-comp' type='month' value={competencia} onChange={(e) => setCompetencia(e.target.value)} className='w-full px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-md text-sm text-white' />
        </div>
        <div className='col-span-2'>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='ld-obs'>Observação * <span className='text-slate-500'>(por que a lista mudou)</span></label>
          <textarea id='ld-obs' rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} className='w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white' />
        </div>
      </div>
      <div className='space-y-1.5 text-xs text-slate-400' data-testid='faturas-e-versao'>
        <p className='font-medium text-slate-300'>Substituir a versão não mexe em fatura que já existe, nem em Rascunho.</p>
        <p>A razão não depende do motor: fatura já existente bloqueia a regeração, e Rascunho não é exceção — a geração recusa quando já há fatura não cancelada para o mesmo contrato e mês, e o motor só roda dentro da geração. Regra conferida na geração em 06/10/2026 (<code>invoices.service</code>, a trava de fatura ativa por contrato e mês).</p>
        {motor && (
          <p data-testid='faturas-e-versao-motor'>
            {motor.ativo
              ? <>O motor deste grupo está <strong>ligado</strong>{motor.ativoEm ? ` desde ${fmtDateTime(motor.ativoEm)}` : ''}, então a geração consulta a versão viva. A garantia acima continua de pé: ela vem da trava de regeração, não do motor.</>
              : <>O motor deste grupo está <strong>desligado</strong>, o que acrescenta uma segunda razão, independente da primeira: com ele desligado a geração nem chega a consultar a versão.</>}
          </p>
        )}
        <p>O único caminho para a divisão nova alcançar um mês já faturado é cancelar a fatura e gerar de novo. Aí a geração usa a versão viva, porque a resolução de versão lê só as não substituídas.</p>
      </div>
      {/* Tom segue o ESTADO: âmbar só quando medido desligado, que é quando a fatura sai sem procedência. */}
      <p
        className={`rounded-lg border px-3 py-2 text-xs ${motor && !motor.ativo ? `${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}` : 'border-slate-700 text-slate-300'}`}
        data-testid='procedencia-da-fatura'
      >
        <strong>A fatura guarda de qual versão veio</strong>, em <code>invoices."rateioVersaoId"</code>, desde 06/10/2026. O campo vem do RESULTADO do motor, não de uma segunda consulta, e por construção só é preenchido quando o motor aplicou: em toda recusa, e em tudo que foi gerado antes dessa data, ele é nulo. Nesses casos o percentual segue apenas na descrição, que é texto de exibição, e duas versões com o mesmo percentual continuam indistinguíveis.
        {motor && (
          <span data-testid='procedencia-da-fatura-motor'>
            {motor.ativo
              ? ' Com o motor deste grupo ligado, toda fatura que ele aplicar daqui para a frente carimba a versão.'
              : ' O motor deste grupo está desligado, então as faturas que saírem agora não carimbam versão nenhuma.'}
          </span>
        )}
      </p>
    </CascaDeModal>
  );
}

/** Invólucro da tela de grupos: lê o rateio (mesmo cache do painel) e mostra o painel e a edição. */
export default function ListasDoRateio({ clientId, contracts, nome, podeEditar, onGravado, abrirAplicarAoLer, onAplicado, semHistorico }: {
  clientId: number;
  contracts: AssinaturaDoGrupo[];
  nome: (clientId: number) => string;
  podeEditar: boolean;
  onGravado: () => void;
  /** Recém-saído do "Começar a dividir": abre o "Aplicar as cotas" assim que o rateio novo for lido, se precisar. */
  abrirAplicarAoLer?: boolean;
  /** As cotas foram aplicadas em pelo menos uma assinatura e o modal fechou: a tela relê contratos e rateio. */
  onAplicado?: () => void;
  /** Na gaveta do rateio o histórico tem aba própria. */
  semHistorico?: boolean;
}) {
  const { rateio } = useRateio(clientId);
  const [editando, setEditando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [aplicou, setAplicou] = useState(false);
  const rrLido = rateio && rateio.temRateio !== false ? (rateio as ComRateio) : null;
  const precisa = !!rrLido && precisaAplicar(rrLido as any);
  // Abre UMA vez, quando o rateio novo chega e há cota a aplicar.
  const [jaAbriu, setJaAbriu] = useState(false);
  useEffect(() => {
    if (abrirAplicarAoLer && podeEditar && precisa && !jaAbriu) { setAplicando(true); setJaAbriu(true); }
  }, [abrirAplicarAoLer, podeEditar, precisa, jaAbriu]);
  if (!rrLido) return null;
  const rr = rrLido;
  const divergentes = rr.linhas.filter((l) => l.valorAcordado != null && Math.round(l.valorCobrado * 100) !== Math.round(l.valorAcordado * 100));
  return (
    <>
      {/* O cobrado não honra o acordado: o motor recusaria essas faturas. A cota se aplica daqui (09/10/2026). */}
      {podeEditar && precisa && (
        <div className={`mb-3 flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${TONE_BORDER.warning} ${TONE_SURFACE.warning}`} data-testid='porta-aplicar-cotas'>
          <p className={`flex-1 ${TONE_TEXT.warning}`}>
            Os contratos de {divergentes.map((l) => nome(l.clientId)).join(', ')} não carregam a cota acordada. O motor só aplica a divisão quando os dois batem.
          </p>
          <button type='button' onClick={() => setAplicando(true)} className='shrink-0 text-xs px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200'>
            Aplicar as cotas nos contratos
          </button>
        </div>
      )}
      <PainelDasListas rateio={rr} contracts={contracts} nome={nome} onEditar={podeEditar ? () => setEditando(true) : undefined} semHistorico={semHistorico} />
      {editando && (
        <EditarListasDoRateio rateio={rr} contracts={contracts} nome={nome} onClose={() => setEditando(false)} onGravado={() => { setEditando(false); onGravado(); }} />
      )}
      {aplicando && (
        <AplicarAsCotas rateio={rr as any} contracts={contracts as any} nome={nome} onClose={() => { setAplicando(false); if (aplicou) onAplicado?.(); }} onAplicado={() => setAplicou(true)} />
      )}
    </>
  );
}
